export interface BackoffConfig {
  initialDelay?: number;
  maxDelay?: number;
  multiplier?: number;
  maxRetries?: number;
  jitter?: boolean;
}

const DEFAULT_CONFIG: Required<BackoffConfig> = {
  initialDelay: 1000,
  maxDelay: 30000,
  multiplier: 2,
  maxRetries: 10,
  jitter: true,
};

export class ExponentialBackoff {
  private config: Required<BackoffConfig>;
  private attempt = 0;
  private currentDelay: number;

  constructor(config: BackoffConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.currentDelay = this.config.initialDelay;
  }

  reset(): void {
    this.attempt = 0;
    this.currentDelay = this.config.initialDelay;
  }

  next(): number {
    const delay = this.getDelay();
    this.attempt++;
    this.updateDelay();
    return delay;
  }

  canRetry(): boolean {
    return this.attempt < this.config.maxRetries;
  }

  getAttempt(): number {
    return this.attempt;
  }

  private getDelay(): number {
    if (this.config.jitter) {
      // Add random jitter (0-25% of current delay) to prevent thundering herd
      const jitter = Math.random() * 0.25 * this.currentDelay;
      return Math.min(this.currentDelay + jitter, this.config.maxDelay);
    }
    return this.currentDelay;
  }

  private updateDelay(): void {
    this.currentDelay = Math.min(
      this.currentDelay * this.config.multiplier,
      this.config.maxDelay
    );
  }
}

export async function withExponentialBackoff<T>(
  fn: () => Promise<T>,
  config?: BackoffConfig,
  shouldRetry?: (error: unknown, attempt: number) => boolean
): Promise<T> {
  const backoff = new ExponentialBackoff(config);

  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (error) {
      if (!backoff.canRetry()) {
        throw error;
      }

      if (shouldRetry && !shouldRetry(error, backoff.getAttempt())) {
        throw error;
      }

      const delay = backoff.next();
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

/**
 * Synchronous version of withExponentialBackoff for cases where async/await is not suitable.
 * Uses busy-waiting for delays, so should only be used for very short delays.
 *
 * @param fn Function to execute that returns T | null (null indicates failure/retry needed)
 * @param config Backoff configuration
 * @param shouldRetry Optional function to determine if retry should continue based on result
 * @returns The result from fn() or null if max retries exceeded
 */
export function withExponentialBackoffSync<T>(
  fn: () => T | null,
  config?: BackoffConfig,
  shouldRetry?: (result: T | null, attempt: number) => boolean
): T | null {
  const backoff = new ExponentialBackoff(config);

  while (backoff.canRetry()) {
    const result = fn();

    // If we got a valid result, return it
    if (result !== null) {
      return result;
    }

    // Check if we should retry based on the result
    if (shouldRetry && !shouldRetry(result, backoff.getAttempt())) {
      return fn(); // Make final attempt before giving up
    }

    // Wait before retrying (synchronous wait)
    const delay = backoff.next();
    const start = Date.now();
    while (Date.now() - start < delay) {
      // Synchronous wait
    }
  }

  // Max retries reached, make final attempt
  return fn();
}
